import * as R from 'ramda';
import { ApiReducer, makeApiAction, makeApiReducer, makeRsaaTypes } from '../../../../core/api/rsaa';
import { addError, addLoading, noLoadingNoError, getLoading } from '../../../../core/api/module';
import { Endpoints } from '../../../../core/api/endpoints';
import { AppState } from '../../../../core/redux/appState';
import { ActionType } from '../../../../core/redux/actionType';


const KEY_ACTION = ActionType.SetDevPlanCollaboratorAcceptedRequest;
const ACTIONS    = [
  ActionType.SetDevPlanCollaboratorAcceptedRequest,
  ActionType.SetDevPlanCollaboratorAcceptedResponse,
  ActionType.SetDevPlanCollaboratorAcceptedError,
];


type Payload = string;


type Meta = {
  projectId: number;
  email: string;
};


/**
 * Dispatch this to retrieve all dev plan collaborators for the current project/participant pair
 *
 * @param projectId - the database Id for the project the dev plan for which the collaborators should be marked
 * @param participantId - the database Id for the participant for whom this person should be marked as having accepted
 * their invite
 * @param email - the collaborator's email address
 */
export const setDevPlanCollaboratorAcceptedAction = (
  projectId: number,
  participantId: number,
  email: string,
) => makeApiAction<Payload, Meta>(
  {
    method:   'POST',
    endpoint: Endpoints.SetDevPlanCollaboratorAccepted,
    headers:  { 'content-type': 'application/json' },
    body:     JSON.stringify(
      {
        projectId:     projectId,
        participantId: participantId,
        email:         email,
      },
    ),
    types:    makeRsaaTypes<Payload, Meta>(
      ACTIONS,
      {
        projectId: projectId,
        email:     email,
      },
    ),
    bailout:  (state: AppState) => !!getLoading(state, KEY_ACTION, `${projectId}-${email}`),
  },
);

const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const { projectId, email } = action.meta;

  const metaTag = `${projectId}-${email}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  const successState = noLoadingNoError(state, KEY_ACTION, metaTag);

  return R.assocPath(
    ['devPlan', 'currentCollaborator', 'currentProject', 'accepted'],
    true,
    successState,
  );
};

export const setDevPlanCollaboratorAcceptedReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
